import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DescriptionIcon from "@mui/icons-material/Description";
import { useState } from "react";
import OnboardingUpload from "../components/OnboardingUpload";
import OnboardingFileDetail from "../components/OnboardingFileDetail";
import Labels from "../labels/Labels";

async function fetchBulkOnboardings() {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/onboarding/bulk`
  );
  return response;
}

async function fetchEmployers() {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/employer/all?excludeRelations=true`
  );
  return response;
}

export default function BulkOnboarding() {
  const [onboardingFileDetail, setOnboardingFileDetail] = useState();
  const [openUploadDlg, setOpenUploadDlg] = useState(false);
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const columns = [
    {
      field: "id",
      headerName: Labels.getLabel("bulkOnboarding.column.id", "id"),
      width: 10,
      hide: true,
    },
    {
      field: "employerName",
      headerName: Labels.getLabel(
        "bulkOnboarding.column.employerName",
        "Employer"
      ),
      width: 200,
    },
    {
      field: "filename",
      headerName: Labels.getLabel("bulkOnboarding.column.filename", "Filename"),
      width: 350,
    },
    {
      field: "processStatus",
      headerName: Labels.getLabel(
        "bulkOnboarding.column.processStatus",
        "Status"
      ),
      width: 150,
      editable: false,
    },
    {
      field: "numOfRecords",
      headerName: Labels.getLabel(
        "bulkOnboarding.column.numOfRecords",
        "Number of records"
      ),
      width: 150,
      editable: false,
    },
    {
      field: "viewDetails",
      headerName: Labels.getLabel(
        "bulkOnboarding.column.viewDetails",
        "View Details"
      ),
      width: 100,
      renderCell: (params) => (
        <div key={"action." + params.id}>
          <IconButton
            key={"view." + params.id}
            onClick={() => {
              setOnboardingFileDetail({ ...params.row });
            }}
          >
            <VisibilityIcon />
          </IconButton>
        </div>
      ),
    },
  ];

  const { data, isLoading } = useQuery(
    ["bulk_onboardings"],
    fetchBulkOnboardings
  );

  const { data: employersData, isLoading: isLoadingEmployers } = useQuery(
    ["employers"],
    fetchEmployers,
    { staleTime: 5 * 60 * 1000 }
  );

  const employerIdToName = Object.fromEntries(
    (employersData?.data || []).map((employer) => [
      employer.idEmployer,
      employer.name,
    ])
  );

  const filteredRows = selectedEmployer
    ? (data?.data || []).filter(
        (row) =>
          row.employerName === employerIdToName[selectedEmployer.idEmployer]
      )
    : data?.data || [];

  const queryClient = useQueryClient();

  const uploadDialog = () => {
    return (
      <Dialog open={openUploadDlg} fullWidth onClose={() => {}}>
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <UploadFileIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel(
                "bulkOnboarding.uploadDialogTitle",
                "Upload Onboarding File"
              )}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setOpenUploadDlg(false);
              queryClient.invalidateQueries({ queryKey: ["bulk_onboardings"] });
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <OnboardingUpload
            setOpen={setOpenUploadDlg}
            employerId={selectedEmployer?.idEmployer}
          />
        </DialogContent>
      </Dialog>
    );
  };

  const onboardingFileDetailDialog = (event) => {
    return (
      <Dialog
        open={onboardingFileDetail !== "undefined"}
        fullWidth
        onClose={() => {}}
        maxWidth="xl"
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DescriptionIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel(
                "bulkOnboarding.detailDialogTitle",
                "Onboarding File Detail"
              )}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setOnboardingFileDetail();
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <OnboardingFileDetail onboardingInfo={onboardingFileDetail} />
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {isLoading ? (
        ""
      ) : (
        <Grid container p={0} m={0} spacing={0}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Typography variant="h5">
                {Labels.getLabel("bulkOnboarding.title", "Bulk Onboardings")}
              </Typography>
              <Autocomplete
                sx={{ minWidth: 300 }}
                options={employersData?.data || []}
                getOptionLabel={(option) =>
                  `${option.name || ""} [${option.idEmployer || ""}]`
                }
                value={selectedEmployer}
                onChange={(event, newValue) => setSelectedEmployer(newValue)}
                loading={isLoadingEmployers}
                clearOnBlur={false}
                isOptionEqualToValue={(option, value) =>
                  option.idEmployer === value?.idEmployer
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={Labels.getLabel(
                      "bulkOnboarding.filterByEmployer",
                      "Filter by Employer"
                    )}
                    size="small"
                  />
                )}
              />
              <Button
                variant="outlined"
                size="small"
                sx={{ marginLeft: "10px" }}
                onClick={() => setOpenUploadDlg(true)}
              >
                {Labels.getLabel(
                  "bulkOnboarding.uploadButton",
                  "Upload Onboarding File"
                )}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ width: "100%", height: "80vh" }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              getRowHeight={() => 25}
              density="compact"
              disableDensitySelector
              disableColumnSelector
              disableColumnFilter
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </Grid>
        </Grid>
      )}
      {onboardingFileDetail ? onboardingFileDetailDialog() : ""}
      {openUploadDlg ? uploadDialog() : ""}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFeedback({ ...feedback, open: false })}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
